import React, { createContext, useEffect, useState } from 'react';
import useSessionStorage from '../hooks/use-session-storage';
import { fsDocListener } from '../firebase/firestore';

export const FeatureContext = createContext();

/**
 * Provider for feature flags.
 *  ~ Test Harness (testing), overrides ->
 *  ~ Firestore feature flags, overrides ->
 *  ~ Hard coded flags.
 *
 * @param {Object} defaultFeatures object defining the fallback feature flags when nothing
 * none are found in session storage.
 * @returns {Object} object with keys corresponding to features and values corresponding
 * to true, if the feature is 'on', and false if the feature is 'off'
 */
const FeatureProvider = ({ children, defaultFeatureFlags = {} }) => {
  const [featureFlags, setFeatureFlags] = useState(defaultFeatureFlags); // default hard coded flags

  // not setting everything in sessionStorage here, because we dont want to make all our flags public
  // use these like a provider, and give both to allow you to distinguish between what is local/remote
  const [cachedFeatureFlags, setCachedFeatureFlags] =
    useSessionStorage('featureFlags');
  const [firebaseFlags, setFirebaseFlags] = useState({});

  // start listening to firebase flags
  useEffect(() => {
    const unsub = fsDocListener('config/features', (snap) => {
      const data = snap.data();
      setFirebaseFlags(data);
    });

    return () => {
      unsub();
    };
  }, []);

  useEffect(() => {
    const updated = {
      ...defaultFeatureFlags, // hard coded flags
      ...firebaseFlags, // firebase flags
      ...cachedFeatureFlags, // test harness / session storage
    };
    setFeatureFlags(updated);
  }, [firebaseFlags, cachedFeatureFlags]);

  return (
    <FeatureContext.Provider
      value={{ featureFlags, cachedFeatureFlags, setCachedFeatureFlags }}
    >
      {children}
    </FeatureContext.Provider>
  );
};

export default FeatureProvider;
